import {
  CalendarAggregatesResponse,
  CalendarBetweenDatesResponse,
  CalendarCountsResponse,
  DateUtils,
  Route,
  populateRoute,
} from "@repo/common"
import { queryOptions } from "@tanstack/react-query"
import { request } from "../api"

async function fetchCalendarBetweenDates(
  storeId: string,
  start: string,
  end: string,
): Promise<CalendarBetweenDatesResponse> {
  return request<CalendarBetweenDatesResponse>({
    url: populateRoute(Route.CALENDAR__BETWEEN_DATES, storeId),
    params: { start, end },
    method: "GET",
  })
}

async function fetchCalendarCounts(
  storeId: string,
  start: string,
  end: string,
): Promise<CalendarCountsResponse> {
  return request<CalendarCountsResponse>({
    url: populateRoute(Route.CALENDAR__COUNTS, storeId),
    params: { start, end },
    method: "GET",
  })
}

async function fetchCalendarAggregates(
  storeId: string,
  start: string,
  end: string,
): Promise<CalendarAggregatesResponse> {
  return request<CalendarAggregatesResponse>({
    url: populateRoute(Route.CALENDAR__AGGREGATES, storeId),
    params: { start, end },
    method: "GET",
  })
}

// Calendar data changes on the order of minutes, not seconds. A modest
// staleTime lets the proactive prefetch (and adjacent-period prefetch) actually
// prevent refetches — without it (staleTime: 0) prefetched data is immediately
// stale and re-fetched on navigation/focus, re-showing the loading skeleton.
const CALENDAR_STALE_TIME = 60_000

export const calendarQueries = {
  betweenDates: (storeId: string, start: Date, end: Date) => {
    const startKey = DateUtils.getDateKey(DateUtils.getStartOfDay(start))
    const endKey = DateUtils.getDateKey(DateUtils.getEndOfDay(end))
    return queryOptions({
      queryKey: ["calendar", "betweenDates", storeId, startKey, endKey],
      queryFn: () => fetchCalendarBetweenDates(storeId, startKey, endKey),
      staleTime: CALENDAR_STALE_TIME,
      refetchOnWindowFocus: false,
    })
  },
  counts: (storeId: string, start: Date, end: Date) => {
    const startKey = DateUtils.getDateKey(DateUtils.getStartOfDay(start))
    const endKey = DateUtils.getDateKey(DateUtils.getEndOfDay(end))
    return queryOptions({
      queryKey: ["calendar", "counts", storeId, startKey, endKey],
      queryFn: () => fetchCalendarCounts(storeId, startKey, endKey),
    })
  },
  aggregates: (storeId: string, start: Date, end: Date) => {
    const startKey = DateUtils.getDateKey(DateUtils.getStartOfDay(start))
    const endKey = DateUtils.getDateKey(DateUtils.getEndOfDay(end))
    return queryOptions({
      queryKey: ["calendar", "aggregates", storeId, startKey, endKey],
      queryFn: () => fetchCalendarAggregates(storeId, startKey, endKey),
      staleTime: CALENDAR_STALE_TIME,
      refetchOnWindowFocus: false,
    })
  },
}
